import React, { useEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import { min, max } from 'd3-array';
import { scaleLinear } from 'd3-scale';
import { line, curveNatural } from 'd3-shape';

import { useMediaQuery } from '~/hooks/use-media-query';

import styles from '~/components/data-vis/charts.module.scss';

const MONTHS = ['Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct'];

const ITEMS = [
  {
    feature: () => 'soil',
    header: 'Collect your lawn soil sample',
    iconId: 'soil-icon',
    iconSrc: () => '/icons/applications/soil-icon-2.svg',
    title: 'Soil test',
    xAxis: 4.3,
  },
  {
    feature: (sourceEngine) =>
      sourceEngine === 'granular' ? 'seedBag' : 'pouch',
    header: 'Fertilize to boost spring growth',
    iconId: 'spring-fert-icon',
    iconSrc: (sourceEngine) =>
      sourceEngine === 'granular'
        ? '/icons/applications/granular-icon-2.svg'
        : '/icons/applications/fert-icon-2.svg',
    title: 'Spring fertilizer',
    xAxis: 5.2,
  },
  {
    feature: () => 'weed',
    header: 'Treat & pull weeds early',
    iconId: 'weed-icon',
    iconSrc: () => '/icons/applications/weed-icon-2.svg',
    title: 'Weeding',
    xAxis: 6.1,
  },
  {
    feature: () => 'pet',
    header: 'Fix brown spots from pets',
    iconId: 'pet-icon',
    iconSrc: () => '/icons/applications/paw-icon-2.svg',
    title: 'Pet patch',
    xAxis: 6.9,
  },
  {
    feature: (sourceEngine) =>
      sourceEngine === 'granular' ? 'seedBag' : 'pouch',
    header: 'Nourish through hot weather stress',
    iconId: 'summer-fert-icon',
    iconSrc: (sourceEngine) =>
      sourceEngine === 'granular'
        ? '/icons/applications/granular-icon-2.svg'
        : '/icons/applications/fert-icon-2.svg',
    title: 'Summer fertilizer',
    xAxis: 7.8,
  },
  {
    feature: () => 'seed',
    header: 'Seed to thicken in spring or fall',
    iconId: 'seed-icon',
    iconSrc: () => '/icons/applications/seed-icon-2.svg',
    title: 'Fall seeding',
    xAxis: 8.9,
  },
  {
    feature: (sourceEngine) =>
      sourceEngine === 'granular' ? 'seedBag' : 'pouch',
    header: 'Fertilize for winter resilience',
    iconId: 'fall-fert-icon',
    iconSrc: (sourceEngine) =>
      sourceEngine === 'granular'
        ? '/icons/applications/granular-icon-2.svg'
        : '/icons/applications/fert-icon-2.svg',
    title: 'Fall fertilizer',
    xAxis: 9.7,
  },
];

const LawnApplicationsChart = ({
  featuresList,
  grassType,
  historicalData,
  mainColor,
  selectedBundleTitle,
  sourceEngine,
}) => {
  const isDesktop = useMediaQuery('(min-width: 769px)');
  const [activeItem, setActiveItem] = useState(ITEMS[0]);
  const bundleTitleRef = useRef(selectedBundleTitle);

  useEffect(() => {
    if (selectedBundleTitle !== bundleTitleRef.current) {
      setActiveItem(ITEMS[0]);
      bundleTitleRef.current = selectedBundleTitle;
    }
  }, [selectedBundleTitle]);

  const margin = { top: isDesktop ? 60 : 80, right: 16, bottom: 30, left: 16 };
  const width = isDesktop ? 860 : 320;
  const height = isDesktop ? 250 : 180;

  // Using historical data but slicing out the months we actually want to render
  const parsedHistorical = parseData(historicalData.slice(2, 10));
  const scaleData = parsedHistorical;

  // Adding beginning and end months set to 0 so the line starts and ends along x-axis
  const renderedScaleData = [{ x: 2, y: 0 }, ...scaleData, { x: 11, y: 0 }];
  const renderedLinearData = isDesktop ? renderedScaleData : scaleData;

  const x = scaleLinear()
    .domain([
      min(renderedLinearData, (d) => d.x),
      max(renderedLinearData, (d) => d.x),
    ])
    .range([margin.left, width - margin.right])
    .nice();
  const y = scaleLinear()
    .domain([0, max(renderedLinearData, (d) => d.y)])
    .range([height - margin.bottom, margin.top])
    .nice();

  const linePath = line()
    .x((d) => x(d.x))
    .y((d) => y(d.y))
    .curve(curveNatural);

  return (
    <div className={cx('panel', styles.applicationsChart)}>
      <p className={cx(styles.applicationsHeader)}>{activeItem.header}</p>

      <div className={styles.legend}>
        <svg viewBox=" 0 0 20 1">
          <path
            d="M0, 0 L20, 0"
            stroke={mainColor}
            strokeWidth="2"
            strokeDasharray="2 5"
          />
        </svg>
        <p>Growing season</p>
      </div>

      <svg
        viewBox={`0 0 ${width} ${height}`}
        xmlns="http://www.w3.org/2000/svg"
      >
        <GradientBackground
          chartHeight={height}
          height={height - margin.top - margin.bottom}
          isDesktop={isDesktop}
          margin={margin}
          width={isDesktop ? width - margin.left - margin.right : width}
        />

        <GrowthLine
          color={mainColor}
          data={renderedScaleData}
          linePath={linePath}
          x={x}
          y={y}
        />

        {ITEMS.map((item) => (
          <ApplicationItem
            activeItem={activeItem}
            featuresList={featuresList}
            grassType={grassType}
            isDesktop={isDesktop}
            item={item}
            key={item.title}
            setActiveItem={setActiveItem}
            sourceEngine={sourceEngine}
            xScale={x}
          />
        ))}

        <TodayFlag
          height={height}
          isDesktop={isDesktop}
          margin={margin}
          xScale={x}
        />

        <AxisBottom height={height} margin={margin} xScale={x} />
      </svg>
    </div>
  );
};

export default LawnApplicationsChart;

const GradientBackground = ({
  chartHeight,
  height,
  isDesktop,
  margin,
  width,
}) => {
  return (
    <g transform={`translate(${isDesktop ? margin.left : 0}, ${margin.top})`}>
      <defs>
        <linearGradient id="bkgd-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="rgba(170, 218, 41, 0.00)" />
          <stop offset="100%" stopColor="rgba(170, 218, 41, 0.30)" />
        </linearGradient>
      </defs>
      <rect height={height} width={width} fill="url(#bkgd-gradient)" />
    </g>
  );
};

const GrowthLine = ({ data, color, linePath, x, y }) => (
  <path
    className={styles.linePath}
    fill="#38996C"
    fillOpacity="0.17"
    stroke={color}
    strokeWidth="2"
    strokeDasharray="2 5"
    d={linePath(data)}
  />
);

const ApplicationItem = ({
  activeItem,
  featuresList,
  grassType,
  isDesktop,
  item,
  setActiveItem,
  sourceEngine,
  xScale,
}) => {
  // Do not show certain icons based on sourceEngine
  // or if the feature is not in the plan's featuresList
  if (
    (sourceEngine === 'granular' && item.iconId === 'summer-fert-icon') ||
    (featuresList && !featuresList.includes(item.feature(sourceEngine)))
  ) {
    return null;
  }

  const selectedItem = activeItem.title === item.title;

  const topAlign = isDesktop ? 40 : 45;
  const pathHeight = isDesktop ? 160 : 90;
  const pathTransform = isDesktop ? '20' : '15';

  const circleRadius = () => {
    if (isDesktop) {
      return selectedItem ? '22.5' : '20';
    } else {
      return selectedItem ? '20' : '16';
    }
  };

  const imageDims = () => {
    if (isDesktop) {
      return selectedItem ? '45' : '40';
    } else {
      return selectedItem ? '40' : '32';
    }
  };

  return (
    <g transform={`translate(${xScale(item.xAxis)}, ${topAlign})`}>
      <path
        d={`M0,0L0 ${pathHeight}`}
        stroke="var(--secondary-dark-color)"
        strokeWidth="1"
        transform={`translate(0, ${pathTransform})`}
      />

      <defs>
        <pattern id={item.iconId} x="0" y="0" height="1" width="1">
          <image
            alt={item.title}
            x="0%"
            y="0%"
            height={imageDims()}
            width={imageDims()}
            xlinkHref={item.iconSrc(sourceEngine)}
          ></image>
        </pattern>
      </defs>
      <circle
        r={circleRadius()}
        fill={`url(#${item.iconId})`}
        filter={
          selectedItem ? 'drop-shadow(0px 3px 7px rgba(0, 0, 0, 0.25))' : ''
        }
        stroke={selectedItem ? 'var(--secondary-dark-color)' : 'var(--gray-5)'}
        strokeWidth={selectedItem ? 2 : '1.2'}
        onClick={() => setActiveItem(item)}
        className={styles.applicationItem}
      >
        <title>{item.title}</title>
      </circle>
    </g>
  );
};

const TodayFlag = ({ height, isDesktop, margin, xScale }) => {
  const flagColor = '#FF8A00';
  const today = new Date();
  const month = today.getMonth() + 1;
  const date = today.getDate();

  // Would be partially or fully off the chart
  if (month < 2 || month > 10) {
    return null;
  }

  // Build decimal to move flag along axis for each month
  function daysInCurrentMonth(month, year) {
    return new Date(year, month, 0).getDate();
  }
  const daysInMonth = daysInCurrentMonth(month, new Date().getFullYear());
  const mappedDay = Math.ceil((date / daysInMonth) * 9); // maps number from 1 - 9 for tenths position
  const currentDatePosition = `${month}.${mappedDay}`;

  const pathHeight = isDesktop ? '-50' : '-20';

  return (
    <g
      className={styles.todayFlag}
      transform={`translate(${xScale(currentDatePosition)}, ${height - margin.bottom})`}
    >
      <rect
        x="-21"
        y={isDesktop ? '-67' : '-37'}
        id="today"
        height="17"
        width="42"
        fill="white"
        stroke={flagColor}
        strokeWidth="1"
      />
      <text
        x="-14"
        y={isDesktop ? '-55' : '-25'}
        fill={flagColor}
        fontSize="1rem"
        fontWeight={700}
      >
        Today
      </text>
      <path d={`M0,0l0 ${pathHeight}`} stroke={flagColor} strokeWidth="1" />
      <circle r="3" fill={flagColor} />
    </g>
  );
};

const AxisBottom = ({ height, margin, xScale }) => {
  const ticks = useMemo(() => {
    return xScale.ticks(8).map((value) => ({
      value,
      xOffset: xScale(value),
    }));
  }, [xScale]);

  return (
    <g
      className={styles.axisBottom}
      transform={`translate(0, ${height - margin.bottom})`}
    >
      {ticks.map(({ value, xOffset }) => (
        <g key={value} transform={`translate(${xOffset}, 0)`}>
          <text key={value}>{MONTHS[value - 3]}</text>
        </g>
      ))}
    </g>
  );
};

function parseData(data) {
  if (!data) {
    return [];
  }

  return data.map((d) => {
    return {
      x: parseInt(d.month, 10),
      y: d.value,
    };
  });
}
